import { SHOPIFY_GRAPHQL_API_ENDPOINT } from '../config'

export async function getProducts(pIds) {
  if(pIds.length === 0) {
    return {
      data: {
        products: []
      },
      error: null
    }
  }
  
  try {
    // Convert numeric IDs to GraphQL global IDs
    const globalIds = pIds.map(id => `gid://shopify/Product/${id}`)
    
    const query = `
      query GetProducts($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            title
            handle
            vendor
            status
            productType
            tags
            createdAt
            updatedAt
            publishedAt
            adminGraphqlApiId: id
            featuredImage {
              id
              altText
              url
              width
              height
            }
            images(first: 250) {
              nodes {
                id
                altText
                url
                width
                height
                adminGraphqlApiId: id
              }
            }
            options(first: 10) {
              id
              name
              values
              position
            }
            variants(first: 100) {
              nodes {
                id
                sku
                price
                compareAtPrice
                title
                taxable
                position
                createdAt
                updatedAt
                inventoryPolicy
                inventoryQuantity
                inventoryItem {
                  id
                }
                adminGraphqlApiId: id
                selectedOptions {
                  name
                  value
                }
                image {
                  id
                }
              }
            }
          }
        }
      }
    `
    
    const response = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: { ids: globalIds },
      }),
    })
    
    const { data, errors } = await response.json()
    
    if (errors) {
      throw new Error(errors.map(error => error.message).join(', '))
    }
    
    // Transform the response to match the expected format
    const products = data.nodes
      .filter(node => node) // Filter out null nodes
      .map(product => {
        // Find the featured image from images array
        const featuredImage = product.featuredImage || (product.images.nodes.length > 0 ? product.images.nodes[0] : null)
        
        return {
          id: parseInt(product.id.replace('gid://shopify/Product/', '')),
          tags: product.tags.join(', '),
          image: featuredImage ? {
            id: parseInt(featuredImage.id.replace('gid://shopify/ProductImage/', '')),
            alt: featuredImage.altText,
            src: featuredImage.url,
            width: featuredImage.width,
            height: featuredImage.height,
            position: 1,
            created_at: product.createdAt,
            product_id: parseInt(product.id.replace('gid://shopify/Product/', '')),
            updated_at: product.updatedAt,
            variant_ids: [],
            admin_graphql_api_id: featuredImage.id
          } : null,
          title: product.title,
          handle: product.handle,
          images: product.images.nodes.map((img, index) => ({
            id: parseInt(img.id.replace('gid://shopify/ProductImage/', '')),
            alt: img.altText,
            src: img.url,
            width: img.width,
            height: img.height,
            position: index + 1,
            created_at: product.createdAt,
            product_id: parseInt(product.id.replace('gid://shopify/Product/', '')),
            updated_at: product.updatedAt,
            variant_ids: [],
            admin_graphql_api_id: img.id
          })),
          status: product.status.toLowerCase(),
          vendor: product.vendor,
          options: product.options.map(option => ({
            id: parseInt(option.id.replace('gid://shopify/ProductOption/', '')),
            name: option.name,
            values: option.values,
            position: option.position,
            product_id: parseInt(product.id.replace('gid://shopify/Product/', ''))
          })),
          variants: product.variants.nodes.map(variant => {
            // Find matching image for variant
            const variantImage = variant.image ? product.images.nodes.find(img => img.id === variant.image.id) : null
            const variantImageId = variantImage ? parseInt(variantImage.id.replace('gid://shopify/ProductImage/', '')) : null
            
            return {
              id: parseInt(variant.id.replace('gid://shopify/ProductVariant/', '')),
              sku: variant.sku || '',
              price: variant.price,
              title: variant.title,
              option1: variant.selectedOptions[0]?.value || null,
              option2: variant.selectedOptions[1]?.value || null,
              option3: variant.selectedOptions[2]?.value || null,
              image_id: variantImageId,
              position: variant.position,
              created_at: variant.createdAt,
              product_id: parseInt(product.id.replace('gid://shopify/Product/', '')),
              updated_at: variant.updatedAt,
              compare_at_price: variant.compareAtPrice,
              inventory_quantity: variant.inventoryQuantity,
              admin_graphql_api_id: variant.id,
            }
          }),
          created_at: product.createdAt,
          updated_at: product.updatedAt,
          product_type: product.productType,
          published_at: product.publishedAt,
          // published_scope: product.publishedScope?.toLowerCase() || 'global',
          admin_graphql_api_id: product.id
        }
      })
    
    return {
      data: {
        products: products
      },
      error: null,
    }
  } catch (error) {
    return {
      data: null,
      error: error.message || error,
    }
  }
}
